'use client';

import React, { memo } from 'react';

import { ExecRisksProvider } from '@explorer/contexts';
import { en as enForms } from '@explorer/forms/presets/locales/en';
import { getRuntimeEnv } from '@explorer/helpers';
import { ExplorerSnackBarProvider, useAppLocale } from '@explorer/hooks';
import { en } from '@explorer/locales/en';
import { ExplorerAPIClientProvider } from '@explorer/query';
import { appConfig } from '@explorer/src';
import { AuthProvider } from '@explorer/stores';
import activeFindings from '@explorer/tables/presets/locales/active-findings.json';
import { en as enTables } from '@explorer/tables/presets/locales/en';
import { ThemeContextProvider } from '@explorer/themes';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { IntlProvider } from 'react-intl';

import { convertToFlatObject } from '../../helpers/intl/convertToFlatObject';
import { ExplorerMonitoringWrapper } from '../ExplorerMonitoringWrapper';
import { ExplorerUserProvider } from '../ExplorerUserProvider';
import { LaunchDarklyWrapper } from '../LaunchDarklyWrapper';

const isTestEnv = getRuntimeEnv('REACT_ENV') === 'test';

const activeFindingsTranslations = convertToFlatObject(
  activeFindings,
  'activeFindings',
);

/**
 * AppProviders
 *
 * Core provider stack without AuthProvider.
 * AuthProvider is intentionally excluded here and should be rendered at a higher level
 * in _app.tsx to prevent it from being remounted when LDProvider wrapper changes.
 *
 * This separation is critical: Auth0Provider must never remount during authentication
 * redirects, otherwise the redirect callback processing is interrupted and the app
 * gets stuck on the loading screen.
 */
export const AppProviders = memo(
  //
  function AppProviders({ children = null }: AppProvidersProps) {
    const appLocale = useAppLocale(appConfig);

    return (
      <ThemeContextProvider appConfig={appConfig}>
        <IntlProvider
          messages={{
            ...en,
            ...enTables,
            ...enForms,
            ...activeFindingsTranslations,
          }}
          locale={appLocale.code}
          defaultLocale={appLocale.code}
        >
          <ExplorerUserProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={appLocale.code}
            >
              <ExplorerSnackBarProvider>
                <ExplorerMonitoringWrapper>
                  <LaunchDarklyWrapper enabled={!isTestEnv}>
                    <ExecRisksProvider>
                      <ExplorerAPIClientProvider>
                        {children}
                      </ExplorerAPIClientProvider>
                    </ExecRisksProvider>
                  </LaunchDarklyWrapper>
                </ExplorerMonitoringWrapper>
              </ExplorerSnackBarProvider>
            </LocalizationProvider>
          </ExplorerUserProvider>
        </IntlProvider>
      </ThemeContextProvider>
    );
  },
);

AppProviders.displayName = 'AppProviders';

/**
 * AuthAppProviders
 *
 * Full provider stack including AuthProvider.
 * Use this for contexts where the component tree is stable (won't remount).
 * For _app.tsx where LDProvider conditionally wraps the tree, use
 * AppProviders and wrap with AuthProvider separately at the top level.
 */
export const AuthAppProviders = memo(
  //
  function AuthAppProviders({ children = null }: AppProvidersProps) {
    return (
      <AuthProvider>
        <AppProviders>{children}</AppProviders>
      </AuthProvider>
    );
  },
);

AuthAppProviders.displayName = 'AuthAppProviders';

/**
 * AppProvidersProps
 */
export interface AppProvidersProps {
  readonly children: JSX.Element[] | JSX.Element | null;
}
